import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = 'https://civicbot-api-h6ffhbgbbeatagf6.westus2-01.azurewebsites.net';

// ─── Category Colors ───────────────────────────────────────────────────────────
const CATEGORY_CONFIG = {
  'UTILITIES':    { color: '#0EA5E9', bg: '#0EA5E910', label: 'Utilities' },
  'PUBLIC WORKS': { color: '#10B981', bg: '#10B98110', label: 'Public Works' },
  'PARKS':        { color: '#8B5CF6', bg: '#8B5CF610', label: 'Parks' },
  'PLANDEV':      { color: '#F59E0B', bg: '#F59E0B10', label: 'Planning & Dev' },
  'DEFAULT':      { color: '#6B7280', bg: '#6B728010', label: 'General' },
};

function getCat(code) {
  return CATEGORY_CONFIG[code] || CATEGORY_CONFIG['DEFAULT'];
}

// ─── Styles ────────────────────────────────────────────────────────────────────
const styles = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    font-family: 'DM Sans', sans-serif;
    background: #0A0F1C;
    color: #E2E8F0;
    min-height: 100vh;
  }

  :root {
    --accent: #3B82F6;
    --accent-light: #60A5FA;
    --accent-dim: #3B82F620;
    --surface: #111827;
    --surface2: #1A2235;
    --surface3: #222D42;
    --border: #1E2D45;
    --border-light: #2A3F5F;
    --text: #E2E8F0;
    --text-muted: #64748B;
    --text-dim: #94A3B8;
    --success: #10B981;
    --warning: #F59E0B;
    --danger: #EF4444;
  }

  .app { display: flex; flex-direction: column; min-height: 100vh; }

  /* ── Login ── */
  .login-wrap {
    display: flex; align-items: center; justify-content: center;
    min-height: 100vh;
    background: radial-gradient(ellipse at 30% 20%, #1E3A5F30 0%, transparent 60%),
                radial-gradient(ellipse at 70% 80%, #0EA5E915 0%, transparent 60%),
                #0A0F1C;
  }
  .login-card {
    width: 420px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: 48px;
    box-shadow: 0 24px 80px #00000060;
  }
  .login-logo {
    display: flex; align-items: center; gap: 12px; margin-bottom: 36px;
  }
  .login-logo-icon {
    width: 42px; height: 42px; border-radius: 10px;
    background: linear-gradient(135deg, #3B82F6, #0EA5E9);
    display: flex; align-items: center; justify-content: center;
    font-size: 20px;
  }
  .login-logo-text { font-size: 22px; font-weight: 600; letter-spacing: -0.5px; }
  .login-logo-text span { color: var(--accent-light); }
  .login-subtitle { color: var(--text-muted); font-size: 14px; margin-bottom: 32px; line-height: 1.5; }
  .login-field { margin-bottom: 16px; }
  .login-label { display: block; font-size: 12px; font-weight: 500; color: var(--text-dim); margin-bottom: 8px; letter-spacing: 0.5px; text-transform: uppercase; }
  .login-input {
    width: 100%; padding: 12px 14px;
    background: var(--surface2); border: 1px solid var(--border-light);
    border-radius: 8px; color: var(--text); font-size: 14px; font-family: inherit;
    outline: none; transition: border-color 0.2s;
  }
  .login-input:focus { border-color: var(--accent); }
  .login-btn {
    width: 100%; padding: 13px;
    background: var(--accent); border: none; border-radius: 8px;
    color: white; font-size: 14px; font-weight: 600; font-family: inherit;
    cursor: pointer; margin-top: 8px; transition: background 0.2s, transform 0.1s;
  }
  .login-btn:hover { background: #2563EB; }
  .login-btn:active { transform: scale(0.99); }
  .login-btn:disabled { background: var(--surface3); color: var(--text-muted); cursor: not-allowed; }
  .login-error { color: var(--danger); font-size: 13px; margin-top: 12px; text-align: center; }

  /* ── Layout ── */
  .header {
    height: 60px; display: flex; align-items: center; justify-content: space-between;
    padding: 0 28px; background: var(--surface);
    border-bottom: 1px solid var(--border); position: sticky; top: 0; z-index: 10;
  }
  .header-logo { display: flex; align-items: center; gap: 10px; font-size: 16px; font-weight: 600; }
  .header-logo-icon {
    width: 30px; height: 30px; border-radius: 7px;
    background: linear-gradient(135deg, #3B82F6, #0EA5E9);
    display: flex; align-items: center; justify-content: center; font-size: 14px;
  }
  .header-right { display: flex; align-items: center; gap: 20px; }
  .header-status {
    display: flex; align-items: center; gap: 6px;
    font-size: 12px; color: var(--text-muted);
  }
  .status-dot {
    width: 7px; height: 7px; border-radius: 50%; background: var(--success);
    box-shadow: 0 0 6px var(--success);
  }
  .logout-btn {
    padding: 7px 14px; background: transparent; border: 1px solid var(--border-light);
    border-radius: 6px; color: var(--text-muted); font-size: 12px; font-family: inherit;
    cursor: pointer; transition: all 0.2s;
  }
  .logout-btn:hover { border-color: var(--accent); color: var(--text); }

  .main { display: flex; flex: 1; }

  .sidebar {
    width: 220px; background: var(--surface); border-right: 1px solid var(--border);
    padding: 20px 12px; display: flex; flex-direction: column; gap: 4px;
  }
  .sidebar-label {
    font-size: 10px; font-weight: 600; color: var(--text-muted);
    letter-spacing: 1px; text-transform: uppercase; padding: 4px 10px; margin-top: 8px;
  }
  .sidebar-item {
    display: flex; align-items: center; gap: 10px; padding: 9px 12px;
    border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 500;
    color: var(--text-muted); transition: all 0.15s; border: 1px solid transparent;
  }
  .sidebar-item:hover { background: var(--surface2); color: var(--text); }
  .sidebar-item.active {
    background: var(--accent-dim); color: var(--accent-light);
    border-color: var(--accent)30;
  }

  .content { flex: 1; padding: 32px; overflow-y: auto; }

  /* ── Page header ── */
  .page-header { margin-bottom: 28px; }
  .page-title { font-size: 22px; font-weight: 600; letter-spacing: -0.5px; }
  .page-subtitle { font-size: 13px; color: var(--text-muted); margin-top: 4px; }

  /* ── Stats row ── */
  .stats-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 28px; }
  .stat-card {
    background: var(--surface); border: 1px solid var(--border);
    border-radius: 12px; padding: 20px 24px;
  }
  .stat-label { font-size: 11px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.5px; }
  .stat-value { font-size: 28px; font-weight: 600; margin-top: 6px; letter-spacing: -1px; }
  .stat-sub { font-size: 12px; color: var(--text-muted); margin-top: 4px; }

  /* ── Card ── */
  .card {
    background: var(--surface); border: 1px solid var(--border);
    border-radius: 12px; overflow: hidden;
  }
  .card-header {
    padding: 18px 24px; border-bottom: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between;
  }
  .card-title { font-size: 14px; font-weight: 600; }
  .card-body { padding: 24px; }

  /* ── SR Template list ── */
  .filter-row {
    display: flex; align-items: center; gap: 12px; margin-bottom: 16px;
  }
  .search-input {
    flex: 1; padding: 9px 14px;
    background: var(--surface2); border: 1px solid var(--border-light);
    border-radius: 8px; color: var(--text); font-size: 13px; font-family: inherit; outline: none;
    transition: border-color 0.2s;
  }
  .search-input:focus { border-color: var(--accent); }
  .filter-btn {
    padding: 9px 14px; background: var(--surface2); border: 1px solid var(--border-light);
    border-radius: 8px; color: var(--text-muted); font-size: 12px; font-family: inherit;
    cursor: pointer; transition: all 0.15s; white-space: nowrap;
  }
  .filter-btn:hover, .filter-btn.active { border-color: var(--accent); color: var(--accent-light); }
  .select-all-row {
    display: flex; align-items: center; justify-content: space-between;
    padding: 10px 16px; background: var(--surface2); border-radius: 8px;
    margin-bottom: 12px; font-size: 12px; color: var(--text-muted);
  }
  .select-all-btn {
    background: none; border: none; color: var(--accent-light); font-size: 12px;
    font-family: inherit; cursor: pointer; font-weight: 500;
  }
  .select-all-btn:hover { text-decoration: underline; }

  .sr-list { display: flex; flex-direction: column; gap: 6px; }
  .sr-item {
    display: flex; align-items: center; gap: 14px; padding: 12px 16px;
    background: var(--surface2); border: 1px solid var(--border);
    border-radius: 8px; cursor: pointer; transition: all 0.15s;
  }
  .sr-item:hover { border-color: var(--border-light); background: var(--surface3); }
  .sr-item.selected { border-color: var(--accent)50; background: var(--accent-dim); }
  .sr-checkbox {
    width: 18px; height: 18px; border-radius: 4px; border: 2px solid var(--border-light);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    transition: all 0.15s;
  }
  .sr-item.selected .sr-checkbox {
    background: var(--accent); border-color: var(--accent);
  }
  .sr-checkbox-check { color: white; font-size: 11px; font-weight: 700; }
  .sr-info { flex: 1; min-width: 0; }
  .sr-name { font-size: 13px; font-weight: 500; }
  .sr-code { font-size: 11px; color: var(--text-muted); font-family: 'DM Mono', monospace; margin-top: 2px; }
  .sr-badge {
    font-size: 10px; font-weight: 600; padding: 3px 8px; border-radius: 4px;
    letter-spacing: 0.3px; flex-shrink: 0;
  }
  .sr-public-badge {
    font-size: 10px; font-weight: 500; padding: 2px 7px; border-radius: 4px;
    background: #10B98115; color: #10B981; flex-shrink: 0;
  }

  /* ── Embed code ── */
  .embed-section { margin-top: 24px; }
  .embed-label { font-size: 12px; font-weight: 600; color: var(--text-dim); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px; }
  .embed-box {
    background: #080D18; border: 1px solid var(--border);
    border-radius: 8px; padding: 16px 20px; position: relative;
  }
  .embed-code {
    font-family: 'DM Mono', monospace; font-size: 12px; color: #7DD3FC;
    white-space: pre-wrap; word-break: break-all; line-height: 1.6;
  }
  .copy-btn {
    position: absolute; top: 12px; right: 12px;
    padding: 6px 12px; background: var(--surface3); border: 1px solid var(--border-light);
    border-radius: 6px; color: var(--text-dim); font-size: 11px; font-family: inherit;
    cursor: pointer; transition: all 0.15s;
  }
  .copy-btn:hover { border-color: var(--accent); color: var(--accent-light); }
  .copy-btn.copied { background: #10B98120; border-color: #10B981; color: #10B981; }

  /* ── Save bar ── */
  .save-bar {
    position: fixed; bottom: 28px; left: 50%; transform: translateX(-50%);
    background: var(--surface); border: 1px solid var(--border-light);
    border-radius: 12px; padding: 14px 20px;
    display: flex; align-items: center; gap: 16px;
    box-shadow: 0 8px 40px #00000080;
    animation: slideUp 0.3s ease;
  }
  @keyframes slideUp { from { opacity: 0; transform: translateX(-50%) translateY(20px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }
  .save-bar-text { font-size: 13px; color: var(--text-muted); }
  .save-bar-text strong { color: var(--text); }
  .save-btn {
    padding: 9px 20px; background: var(--accent); border: none; border-radius: 8px;
    color: white; font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer;
    transition: background 0.2s;
  }
  .save-btn:hover { background: #2563EB; }
  .save-btn.saved { background: var(--success); }

  /* ── Settings ── */
  .settings-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .setting-field { display: flex; flex-direction: column; gap: 6px; }
  .setting-label { font-size: 12px; font-weight: 500; color: var(--text-dim); }
  .setting-input {
    padding: 10px 14px; background: var(--surface2); border: 1px solid var(--border-light);
    border-radius: 8px; color: var(--text); font-size: 13px; font-family: inherit; outline: none;
    transition: border-color 0.2s;
  }
  .setting-input:focus { border-color: var(--accent); }
  .setting-full { grid-column: 1 / -1; }

  /* ── Loading / Empty ── */
  .loading { display: flex; align-items: center; justify-content: center; padding: 60px; gap: 12px; color: var(--text-muted); font-size: 14px; }
  .spinner { width: 20px; height: 20px; border: 2px solid var(--border-light); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.8s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
  .empty { padding: 48px; text-align: center; color: var(--text-muted); font-size: 14px; }

  /* ── Toast ── */
  .toast {
    position: fixed; top: 20px; right: 20px; z-index: 100;
    padding: 12px 18px; border-radius: 8px; font-size: 13px; font-weight: 500;
    animation: fadeIn 0.3s ease;
    box-shadow: 0 4px 20px #00000060;
  }
  .toast.success { background: #10B98120; border: 1px solid #10B981; color: #10B981; }
  .toast.error { background: #EF444420; border: 1px solid #EF4444; color: #EF4444; }
  @keyframes fadeIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }

  /* ── Preview widget ── */
  .widget-preview {
    background: #080D18; border: 1px solid var(--border);
    border-radius: 12px; padding: 20px; margin-top: 16px;
  }
  .widget-preview-title { font-size: 12px; color: var(--text-muted); margin-bottom: 14px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; }
  .widget-bubble {
    width: 52px; height: 52px; border-radius: 50%;
    background: linear-gradient(135deg, #3B82F6, #0EA5E9);
    display: flex; align-items: center; justify-content: center;
    font-size: 22px; cursor: pointer; margin-left: auto;
    box-shadow: 0 4px 20px #3B82F640;
    transition: transform 0.2s;
  }
  .widget-bubble:hover { transform: scale(1.08); }
  .widget-chat {
    background: var(--surface); border: 1px solid var(--border-light);
    border-radius: 12px; padding: 16px; margin-bottom: 12px;
    font-size: 13px; line-height: 1.5;
  }
  .widget-chat-name { font-size: 11px; font-weight: 600; color: var(--accent-light); margin-bottom: 6px; }
`;

// ─── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState('login');
  const [token, setToken] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  if (page === 'login') {
    return (
      <>
        <style>{styles}</style>
        <LoginPage onLogin={(t) => { setToken(t); setPage('templates'); }} showToast={showToast} />
        {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="app">
        <Header onLogout={() => { setToken(null); setPage('login'); }} />
        <div className="main">
          <Sidebar page={page} setPage={setPage} />
          <div className="content">
            {page === 'templates' && <TemplatesPage token={token} showToast={showToast} />}
            {page === 'settings' && <SettingsPage showToast={showToast} />}
            {page === 'embed' && <EmbedPage />}
          </div>
        </div>
      </div>
      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}

// ─── Login ─────────────────────────────────────────────────────────────────────
function LoginPage({ onLogin, showToast }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleLogin(e) {
    e.preventDefault();
    if (!username || !password) { setError('Please enter username and password'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/test/auth`);
      const data = await res.json();
      if (data.success) {
        onLogin(data.tokenPreview);
        showToast('Logged in successfully');
      } else {
        setError('Authentication failed. Check credentials.');
      }
    } catch {
      setError('Cannot connect to CivicBot server. Is it running?');
    }
    setLoading(false);
  }

  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="login-logo">
          <div className="login-logo-icon">🏙️</div>
          <div className="login-logo-text">Civic<span>Bot</span></div>
        </div>
        <p className="login-subtitle">Admin portal for managing your city's AI-powered service request chatbot.</p>
        <form onSubmit={handleLogin}>
          <div className="login-field">
            <label className="login-label">Username</label>
            <input className="login-input" value={username} onChange={e => setUsername(e.target.value)} placeholder="admin@yourcity.gov" autoFocus />
          </div>
          <div className="login-field">
            <label className="login-label">Password</label>
            <input className="login-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" />
          </div>
          <button className="login-btn" type="submit" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
          {error && <p className="login-error">{error}</p>}
        </form>
      </div>
    </div>
  );
}

// ─── Header ────────────────────────────────────────────────────────────────────
function Header({ onLogout }) {
  return (
    <div className="header">
      <div className="header-logo">
        <div className="header-logo-icon">🏙️</div>
        CivicBot Admin
      </div>
      <div className="header-right">
        <div className="header-status">
          <div className="status-dot" />
          Connected to Trimble Unity
        </div>
        <button className="logout-btn" onClick={onLogout}>Sign Out</button>
      </div>
    </div>
  );
}

// ─── Sidebar ───────────────────────────────────────────────────────────────────
function Sidebar({ page, setPage }) {
  const items = [
    { id: 'templates', icon: '☑️', label: 'SR Templates' },
    { id: 'embed', icon: '🔗', label: 'Embed Code' },
    { id: 'settings', icon: '⚙️', label: 'Settings' },
  ];
  return (
    <div className="sidebar">
      <div className="sidebar-label">Navigation</div>
      {items.map(item => (
        <div key={item.id} className={`sidebar-item ${page === item.id ? 'active' : ''}`} onClick={() => setPage(item.id)}>
          <span>{item.icon}</span>
          <span>{item.label}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Templates Page ────────────────────────────────────────────────────────────

function TemplatesPage({ showToast }) {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [aliases, setAliases] = useState({});
  const [editingAlias, setEditingAlias] = useState(null);
  const [aliasInput, setAliasInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('ALL');
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Load saved config
    const savedIds = JSON.parse(localStorage.getItem('civicbot_selected_templates') || '[]');
    const savedAliases = JSON.parse(localStorage.getItem('civicbot_aliases') || '{}');
    setAliases(savedAliases);

    fetch(`${API_BASE}/api/sr-templates`)
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setTemplates(data.templates);
          if (savedIds.length > 0) {
            setSelected(new Set(savedIds));
          } else {
            const pub = new Set(data.templates.filter(t => t.ForPublicSite).map(t => t.ProblemSid));
            setSelected(pub);
          }
        }
      })
      .catch(() => showToast('Failed to load templates', 'error'))
      .finally(() => setLoading(false));
  }, [showToast]);

  const categories = ['ALL', ...new Set(templates.map(t => t.ProbCategory))];

  const filtered = templates.filter(t => {
    const displayName = aliases[t.ProblemSid] || t.Description;
    const matchSearch = displayName.toLowerCase().includes(search.toLowerCase()) ||
                        t.ProblemCode.toLowerCase().includes(search.toLowerCase());
    const matchCat = catFilter === 'ALL' || t.ProbCategory === catFilter;
    return matchSearch && matchCat;
  });

  function toggle(sid) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(sid) ? next.delete(sid) : next.add(sid);
      return next;
    });
    setSaved(false);
  }

  function selectAll() { setSelected(new Set(filtered.map(t => t.ProblemSid))); setSaved(false); }
  function selectNone() {
    setSelected(prev => {
      const next = new Set(prev);
      filtered.forEach(t => next.delete(t.ProblemSid));
      return next;
    });
    setSaved(false);
  }

  function startEditAlias(sid, current) {
    setEditingAlias(sid);
    setAliasInput(aliases[sid] || '');
  }

  function saveAlias(sid) {
    const trimmed = aliasInput.trim();
    setAliases(prev => {
      const next = { ...prev };
      if (trimmed) next[sid] = trimmed;
      else delete next[sid];
      return next;
    });
    setEditingAlias(null);
    setSaved(false);
  }

  function clearAlias(sid) {
    setAliases(prev => {
      const next = { ...prev };
      delete next[sid];
      return next;
    });
    setSaved(false);
  }

  function handleSave() {
    localStorage.setItem('civicbot_selected_templates', JSON.stringify([...selected]));
    localStorage.setItem('civicbot_aliases', JSON.stringify(aliases));
    setSaved(true);
    showToast(`Saved ${selected.size} SR types with ${Object.keys(aliases).length} aliases`);
  }

  const selectedTemplates = templates.filter(t => selected.has(t.ProblemSid));
  const embedCode = generateEmbedCode(selectedTemplates);

  function copyEmbed() {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    showToast('Embed code copied!');
  }

  if (loading) return <div className="loading"><div className="spinner" />Loading SR templates from Trimble Unity...</div>;

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Service Request Templates</div>
        <div className="page-subtitle">Select SR types and optionally set a friendly alias name for the chatbot</div>
      </div>

      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-label">Total Templates</div>
          <div className="stat-value">{templates.length}</div>
          <div className="stat-sub">In Trimble Unity</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Enabled for CivicBot</div>
          <div className="stat-value" style={{color: 'var(--accent-light)'}}>{selected.size}</div>
          <div className="stat-sub">Residents can submit these</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Custom Aliases</div>
          <div className="stat-value" style={{color: '#8B5CF6'}}>{Object.keys(aliases).length}</div>
          <div className="stat-sub">Renamed for residents</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Configure SR Types</div>
          <span style={{fontSize: 12, color: 'var(--text-muted)'}}>{selected.size} of {templates.length} selected</span>
        </div>
        <div className="card-body">
          <div className="filter-row">
            <input className="search-input" placeholder="Search by name or code..." value={search} onChange={e => setSearch(e.target.value)} />
            {categories.map(cat => (
              <button key={cat} className={`filter-btn ${catFilter === cat ? 'active' : ''}`} onClick={() => setCatFilter(cat)}>
                {cat === 'ALL' ? 'All' : getCat(cat).label}
              </button>
            ))}
          </div>

          <div className="select-all-row">
            <span>{filtered.length} templates shown</span>
            <div style={{display: 'flex', gap: 12}}>
              <button className="select-all-btn" onClick={selectAll}>Select All</button>
              <span style={{color: 'var(--border-light)'}}>|</span>
              <button className="select-all-btn" onClick={selectNone}>Deselect All</button>
            </div>
          </div>

          {/* Column headers */}
          <div style={{display:'flex', alignItems:'center', padding:'6px 16px', fontSize:11, color:'var(--text-muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:4}}>
            <div style={{width:32}}></div>
            <div style={{flex:1}}>Cityworks Name</div>
            <div style={{width:220}}>Display Alias (shown in chatbot)</div>
            <div style={{width:120, textAlign:'right'}}>Category</div>
          </div>

                    <div className="sr-list">
            {filtered.map(t => {
              const cat = getCat(t.ProbCategory);
              const isSelected = selected.has(t.ProblemSid);
              const hasAlias = !!aliases[t.ProblemSid];
              const isEditing = editingAlias === t.ProblemSid;

              return (
                <div key={t.ProblemSid} className={`sr-item ${isSelected ? 'selected' : ''}`} onClick={e => { if (!e.target.closest('.alias-area')) toggle(t.ProblemSid); }}>
                  <div className="sr-checkbox">
                    {isSelected && <span className="sr-checkbox-check">✓</span>}
                  </div>
                  <div className="sr-info" style={{flex:1}}>
                    <div className="sr-name">{t.Description}</div>
                    <div style={{display:'flex', alignItems:'center', gap:8, marginTop:4, flexWrap:'wrap'}}>
                      <div className="sr-code">{t.ProblemCode}</div>
                      <div className="alias-area" onClick={e => e.stopPropagation()}>
                        {isEditing ? (
                          <div style={{display:'flex', gap:4, alignItems:'center'}}>
                            <input autoFocus value={aliasInput} onChange={e => setAliasInput(e.target.value)}
                              onKeyDown={e => { if (e.key==='Enter') saveAlias(t.ProblemSid); if (e.key==='Escape') setEditingAlias(null); }}
                              placeholder={t.Description}
                              style={{width:180, padding:'3px 8px', background:'var(--surface3)', border:'1px solid var(--accent)', borderRadius:5, color:'var(--text)', fontSize:11, fontFamily:'inherit', outline:'none'}}
                            />
                            <button onClick={() => saveAlias(t.ProblemSid)} style={{background:'var(--accent)', border:'none', borderRadius:4, color:'white', padding:'3px 8px', cursor:'pointer', fontSize:11}}>✓</button>
                            <button onClick={() => setEditingAlias(null)} style={{background:'var(--surface3)', border:'1px solid var(--border-light)', borderRadius:4, color:'var(--text-muted)', padding:'3px 6px', cursor:'pointer', fontSize:11}}>✕</button>
                          </div>
                        ) : hasAlias ? (
                          <div style={{display:'flex', alignItems:'center', gap:4}}>
                            <span style={{fontSize:11, color:'#8B5CF6', background:'#8B5CF615', padding:'2px 7px', borderRadius:4, border:'1px solid #8B5CF630'}}>→ {aliases[t.ProblemSid]}</span>
                            <button onClick={() => startEditAlias(t.ProblemSid)} style={{background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:11, padding:'0 2px'}}>✏️</button>
                            <button onClick={() => clearAlias(t.ProblemSid)} style={{background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:10, padding:'0 2px'}}>✕</button>
                          </div>
                        ) : (
                          <button onClick={() => startEditAlias(t.ProblemSid)}
                            style={{background:'none', border:'1px dashed var(--border-light)', borderRadius:4, color:'var(--text-muted)', fontSize:10, padding:'2px 8px', cursor:'pointer', fontFamily:'inherit'}}>
                            + alias
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{display:'flex', gap:4, alignItems:'center', flexShrink:0}}>
                    <span className="sr-badge" style={{background: cat.bg, color: cat.color}}>{cat.label}</span>
                    {t.ForPublicSite && <span className="sr-public-badge">Public</span>}
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </div>

      {/* Embed code */}
      <div className="card" style={{marginTop: 20}}>
        <div className="card-header">
          <div className="card-title">Your Embed Code</div>
          <span style={{fontSize: 12, color: 'var(--text-muted)'}}>Paste this on your city website</span>
        </div>
        <div className="card-body">
          <div className="embed-box">
            <pre className="embed-code">{embedCode}</pre>
            <button className={`copy-btn ${copied ? 'copied' : ''}`} onClick={copyEmbed}>
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
        </div>
      </div>

      {/* Save bar */}
      {!saved && (
        <div className="save-bar">
          <span className="save-bar-text"><strong>{selected.size} SR types</strong> · <strong>{Object.keys(aliases).length} aliases</strong></span>
          <button className="save-btn" onClick={handleSave}>Save Configuration</button>
        </div>
      )}
    </div>
  );
}

// ─── Embed Page ────────────────────────────────────────────────────────────────
function EmbedPage() {
  const [copied, setCopied] = useState(false);

  const savedIds = JSON.parse(localStorage.getItem('civicbot_selected_templates') || '[]');
  const code = generateEmbedCode([], savedIds);

  function copy() {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Embed Code</div>
        <div className="page-subtitle">Add CivicBot to your city website in one line of code</div>
      </div>

      <div className="card">
        <div className="card-header"><div className="card-title">Website Widget</div></div>
        <div className="card-body">
          <p style={{fontSize: 13, color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.6}}>
            Paste this script tag anywhere in your city website's HTML, just before the closing <code style={{background: 'var(--surface3)', padding: '2px 6px', borderRadius: 4, fontSize: 12}}>&lt;/body&gt;</code> tag. The chat bubble will appear automatically in the bottom-right corner.
          </p>
          <div className="embed-box">
            <pre className="embed-code">{code}</pre>
            <button className={`copy-btn ${copied ? 'copied' : ''}`} onClick={copy}>
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>

          <div className="widget-preview">
            <div className="widget-preview-title">Live Preview</div>
            <div className="widget-chat">
              <div className="widget-chat-name">CivicBot</div>
              Hi! I'm CivicBot. I can help you submit a service request to the city. What issue would you like to report today?
            </div>
            <div className="widget-bubble">🏙️</div>
          </div>
        </div>
      </div>

      <div className="card" style={{marginTop: 20}}>
        <div className="card-header"><div className="card-title">Standalone Page</div></div>
        <div className="card-body">
          <p style={{fontSize: 13, color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.6}}>
            Share this link directly — on social media, in emails, or as a QR code on city signage.
          </p>
          <div className="embed-box">
            <pre className="embed-code">https://civicbot.io/submit/your-city</pre>
            <button className="copy-btn">Copy</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Settings Page ─────────────────────────────────────────────────────────────
function SettingsPage({ showToast }) {
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('civicbot_settings');
    return saved ? JSON.parse(saved) : {
      cityName: '',
      greeting: 'Hi! I can help you submit a service request. What issue would you like to report?',
      primaryColor: '#3B82F6',
      logoUrl: '',
      cwBaseUrl: 'https://partners.maintain.unity.trimble.com/PARTNER_CentricityGIS',
      cwUser: 'APIUser',
      state: '',
      wkid: 4326,
      zoneName: '',
    };
  });

  const [zones, setZones] = useState([]);
  const [loadingZones, setLoadingZones] = useState(false);

  const US_STATES = [
    ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
    ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['FL','Florida'],['GA','Georgia'],
    ['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],
    ['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
    ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],
    ['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],
    ['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
    ['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
    ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],
    ['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
  ];

  // Load zones when state changes
  useEffect(() => {
    if (!settings.state) { setZones([]); return; }
    setLoadingZones(true);
    fetch(`${API_BASE}/api/state-plane-zones?state=${settings.state}`)
      .then(r => r.json())
      .then(data => {
        if (data.success) setZones(data.zones);
      })
      .finally(() => setLoadingZones(false));
  }, [settings.state]);

  function handleStateChange(state) {
    setSettings(s => ({ ...s, state, wkid: 4326, zoneName: '' }));
  }

  function handleZoneChange(wkid, zoneName) {
    setSettings(s => ({ ...s, wkid: parseInt(wkid), zoneName }));
  }

  function save() {
    localStorage.setItem('civicbot_settings', JSON.stringify(settings));
    showToast('Settings saved');
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Settings</div>
        <div className="page-subtitle">Configure your CivicBot instance</div>
      </div>

      {/* Branding */}
      <div className="card" style={{marginBottom: 20}}>
        <div className="card-header"><div className="card-title">Branding</div></div>
        <div className="card-body">
          <div className="settings-grid">
            <div className="setting-field">
              <label className="setting-label">City Name</label>
              <input className="setting-input" value={settings.cityName} onChange={e => setSettings({...settings, cityName: e.target.value})} placeholder="City of Springfield" />
            </div>
            <div className="setting-field">
              <label className="setting-label">Primary Color</label>
              <div style={{display:'flex', gap:8, alignItems:'center'}}>
                <input className="setting-input" value={settings.primaryColor} onChange={e => setSettings({...settings, primaryColor: e.target.value})} style={{flex:1}} />
                <div style={{width:36, height:36, borderRadius:8, background: settings.primaryColor, border:'1px solid var(--border-light)', flexShrink:0}} />
              </div>
            </div>
            <div className="setting-field setting-full">
              <label className="setting-label">Welcome Message</label>
              <input className="setting-input" value={settings.greeting} onChange={e => setSettings({...settings, greeting: e.target.value})} />
            </div>
          </div>
        </div>
      </div>

      {/* Location & Coordinate System */}
      <div className="card" style={{marginBottom: 20}}>
        <div className="card-header">
          <div className="card-title">Location & Coordinate System</div>
          <span style={{fontSize:12, color:'var(--text-muted)'}}>Required for accurate map placement in Cityworks</span>
        </div>
        <div className="card-body">
          <div className="settings-grid">
            <div className="setting-field">
              <label className="setting-label">State</label>
              <select
                className="setting-input"
                value={settings.state}
                onChange={e => handleStateChange(e.target.value)}
                style={{cursor:'pointer'}}
              >
                <option value="">Select state...</option>
                {US_STATES.map(([code, name]) => (
                  <option key={code} value={code}>{name}</option>
                ))}
              </select>
            </div>

            <div className="setting-field">
              <label className="setting-label">
                State Plane Zone {loadingZones && <span style={{color:'var(--text-muted)', fontSize:10}}> Loading...</span>}
              </label>
              <select
                className="setting-input"
                value={settings.wkid}
                onChange={e => {
                  const zone = zones.find(z => z.wkid === parseInt(e.target.value));
                  handleZoneChange(e.target.value, zone?.name || '');
                }}
                disabled={zones.length === 0}
                style={{cursor: zones.length > 0 ? 'pointer' : 'not-allowed'}}
              >
                <option value={4326}>{zones.length === 0 ? 'Select state first...' : 'Select zone...'}</option>
                {zones.map(z => (
                  <option key={z.wkid} value={z.wkid}>{z.name} (WKID: {z.wkid})</option>
                ))}
              </select>
            </div>

            {/* Show selected WKID confirmation */}
            {settings.wkid && settings.wkid !== 4326 && (
              <div className="setting-field setting-full">
                <div style={{
                  background: '#10B98115', border: '1px solid #10B98130',
                  borderRadius: 8, padding: '10px 14px',
                  fontSize: 13, color: '#10B981', display:'flex', alignItems:'center', gap:8
                }}>
                  ✓ Using <strong>{settings.zoneName}</strong> — WKID {settings.wkid}. Coordinates will be correctly projected in Cityworks.
                </div>
              </div>
            )}

            {/* Fallback manual WKID entry */}
            <div className="setting-field setting-full">
              <label className="setting-label">Or enter WKID manually <span style={{color:'var(--text-muted)', fontWeight:400}}>(advanced)</span></label>
              <input
                className="setting-input"
                type="number"
                value={settings.wkid}
                onChange={e => setSettings({...settings, wkid: parseInt(e.target.value), zoneName: 'Custom'})}
                placeholder="e.g. 102100 for Web Mercator"
              />
              <span style={{fontSize:11, color:'var(--text-muted)', marginTop:4}}>
                Common: Web Mercator = 102100, WGS84 = 4326. Check your ArcGIS map properties for the correct WKID.
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Trimble Unity Connection */}
      <div className="card" style={{marginBottom: 20}}>
        <div className="card-header"><div className="card-title">Trimble Unity Connection</div></div>
        <div className="card-body">
          <div className="settings-grid">
            <div className="setting-field setting-full">
              <label className="setting-label">Cityworks Base URL</label>
              <input className="setting-input" value={settings.cwBaseUrl} onChange={e => setSettings({...settings, cwBaseUrl: e.target.value})} />
            </div>
            <div className="setting-field">
              <label className="setting-label">API Username</label>
              <input className="setting-input" value={settings.cwUser} onChange={e => setSettings({...settings, cwUser: e.target.value})} />
            </div>
            <div className="setting-field">
              <label className="setting-label">API Password</label>
              <input className="setting-input" type="password" placeholder="••••••••••" />
            </div>
          </div>
        </div>
      </div>

      <button className="save-btn" style={{padding: '11px 24px'}} onClick={save}>Save Settings</button>
    </div>
  );
}
// ─── Embed Code Generator ──────────────────────────────────────────────────────
function generateEmbedCode(templates, ids) {
  const templateIds = ids || templates.map(t => t.ProblemSid);
  return `<script src="https://civicbot.io/widget.js"
  data-city-id="your-city-id"
  data-templates="${templateIds.join(',')}"
  data-color="#3B82F6"
  data-greeting="Hi! I can help you submit a service request."
  async>
</script>`;
}
